import React from 'react';
import moment from 'moment';
import ReactApexChart from 'react-apexcharts';
import { buildBloodPressureDayChartModel } from '#utils/bloodPressureReadings';

/**
 * Ten sam wykres co na stronie kalkulatorów (scatter: skurcz / rozkurcz vs dni).
 * `readingsChronological` — najstarszy → najnowszy.
 */
export function BloodPressureReadingsChart({ readingsChronological, lang }) {
  if (!readingsChronological?.length) {
    return <p className='text-muted small mb-0'>Brak zapisanych pomiarów.</p>;
  }

  const model = buildBloodPressureDayChartModel(readingsChronological, lang);
  if (!model) {
    return <p className='text-muted small mb-0'>Brak poprawnych dat w pomiarach.</p>;
  }

  const { pointReadings, dayLabels, sysData, diaData, yMin, yMax, nDays } = model;

  const sysLabel = lang === 'pl' ? 'Skurczowe (mmHg)' : 'Systolic (mmHg)';
  const diaLabel = lang === 'pl' ? 'Rozkurczowe (mmHg)' : 'Diastolic (mmHg)';

  const series = [
    { name: sysLabel, data: sysData },
    { name: diaLabel, data: diaData },
  ];

  const chartRemountKey = `${model.dayKeys.join(',')}-${pointReadings.map((r) => r.id).join('.')}`;

  const tooltipWhen = (reading) => {
    const src = reading.created_at ?? reading.createdAt ?? reading.date;
    if (!src) return '';
    return moment(src).format(lang === 'pl' ? 'DD.MM.YYYY HH:mm' : 'MMM D, YYYY HH:mm');
  };

  const padY = Math.max(8, Math.round((yMax - yMin) * 0.08));
  const yFloor = Math.max(0, Math.floor(yMin - padY));
  const yCeil = Math.ceil(yMax + padY);

  const options = {
    chart: { type: 'scatter', toolbar: { show: false }, zoom: { enabled: false } },
    stroke: { width: 0 },
    colors: ['#dc3545', '#0d6efd'],
    xaxis: {
      type: 'numeric',
      min: -0.5,
      max: Math.max(0, nDays - 1) + 0.5,
      tickAmount: Math.max(1, nDays),
      decimalsInFloat: 0,
      labels: {
        formatter: (val) => {
          const i = Math.round(Number(val));
          if (!Number.isFinite(i) || i < 0 || i >= dayLabels.length) return '';
          return dayLabels[i];
        },
      },
    },
    yaxis: {
      min: yFloor,
      max: yCeil,
      title: { text: 'mmHg' },
      labels: { formatter: (v) => String(Math.round(v)) },
    },
    markers: { size: 6, strokeWidth: 0, hover: { sizeOffset: 2 } },
    grid: { borderColor: '#f1f1f1', padding: { right: 8, left: 8 } },
    legend: { position: 'top' },
    tooltip: {
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex }) => {
        const j = dataPointIndex;
        if (j == null || j < 0 || j >= pointReadings.length) return '';
        const reading = pointReadings[j];
        const when = tooltipWhen(reading);
        const sys = Number(reading.systolic);
        const dia = Number(reading.diastolic);
        const head = when
          ? `<div class="px-2 py-1" style="font-size:12px"><strong>${when}</strong></div>`
          : '';
        return `${head}<div class="px-2 pb-2" style="font-size:12px">
          <span style="color:#dc3545">●</span> ${sysLabel}: <strong>${sys}</strong> mmHg<br/>
          <span style="color:#0d6efd">●</span> ${diaLabel}: <strong>${dia}</strong> mmHg
        </div>`;
      },
    },
  };

  return (
    <ReactApexChart
      key={chartRemountKey}
      options={options}
      series={series}
      type='scatter'
      height={320}
    />
  );
}
